import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Header, Footer } from "../components/Layout";
import { Wallet, ArrowsClockwise, CheckCircle, XCircle, Clock, Sparkle } from "@phosphor-icons/react";

/**
 * Super-admin tool — astrologer withdrawal queue.
 * Lists every withdrawal request, lets the admin mark each as PAID
 * (with an optional payment reference) or REJECT (with a reason).
 *
 * Auth: relies on the same `session_token` cookie the rest of the
 * super-admin tools use. Backend gates with `_is_super_admin(customer)`.
 */

const INR = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const TABS = [
  { id: "pending", label: "Pending", icon: Clock, tone: "amber" },
  { id: "paid", label: "Paid", icon: CheckCircle, tone: "emerald" },
  { id: "rejected", label: "Rejected", icon: XCircle, tone: "rose" },
];

export default function AdminWithdrawalsPage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [refDialog, setRefDialog] = useState(null); // { mode: 'pay'|'reject', withdrawal }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/withdrawals");
      setItems(data?.items || []);
      setSummary(data?.summary || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load withdrawal requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const filtered = items.filter((it) => it.status === tab);

  const markPaid = async (w, payment_reference, note) => {
    setBusyId(w.withdrawal_id);
    try {
      await api.post(`/super-admin/withdrawals/${w.withdrawal_id}/mark-paid`, {
        payment_reference: payment_reference || null,
        note: note || null,
      });
      toast.success(`Marked ${INR(w.amount)} as paid to ${w.astrologer_name}`);
      setRefDialog(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't mark as paid");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (w, note) => {
    setBusyId(w.withdrawal_id);
    try {
      await api.post(`/super-admin/withdrawals/${w.withdrawal_id}/reject`, { note: note || null });
      toast.success(`Rejected request from ${w.astrologer_name}`);
      setRefDialog(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reject");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-withdrawals-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="label-eyebrow">Super-admin · Astrologer payouts</p>
            <h1 className="font-serif-display text-3xl sm:text-5xl mt-2 font-light flex items-center gap-3">
              <Wallet size={36} weight="duotone" className="text-gold" />
              Withdrawal queue
            </h1>
            <p className="text-muted mt-2 max-w-xl text-sm">
              Astrologers see this number in their wallet as &ldquo;pending&rdquo; while it sits here.
              Mark as paid the moment you complete the bank/UPI transfer.
            </p>
          </div>
          <button
            onClick={load}
            className="btn-ghost rounded-full px-4 py-2 text-sm flex items-center gap-2 self-start"
            data-testid="refresh-withdrawals-btn"
          >
            <ArrowsClockwise size={14} /> Refresh
          </button>
        </div>

        {/* Summary tiles */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8" data-testid="withdrawal-summary">
            <SummaryTile label="Pending" count={summary.pending_count} amount={summary.pending_total} tone="amber" />
            <SummaryTile label="Paid" count={summary.paid_count} amount={summary.paid_total} tone="emerald" />
            <SummaryTile label="Avg pending" count="—" amount={summary.pending_count ? summary.pending_total / summary.pending_count : 0} tone="indigo" />
            <SummaryTile label="Total processed" count={summary.pending_count + summary.paid_count} amount={summary.pending_total + summary.paid_total} tone="violet" />
          </div>
        )}

        {/* Status tabs */}
        <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="withdrawal-tabs">
          {TABS.map((t) => {
            const active = tab === t.id;
            const count = items.filter((it) => it.status === t.id).length;
            const TabIcon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition border ${
                  active
                    ? "bg-[#3F2D1B] text-amber-50 border-[#3F2D1B]"
                    : "bg-white text-stone-700 border-stone-300 hover:border-amber-400"
                }`}
                data-testid={`withdrawal-tab-${t.id}`}
              >
                <TabIcon size={14} weight="fill" />
                {t.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-amber-300/20 text-amber-100" : "bg-stone-100 text-stone-600"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className="mt-6 space-y-3">
          {loading && (
            <div className="text-muted text-sm py-12 text-center" data-testid="withdrawals-loading">Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="card-surface rounded-2xl p-10 text-center" data-testid="withdrawals-empty">
              <Sparkle size={22} weight="duotone" className="text-gold mx-auto mb-2" />
              <p className="text-muted text-sm">No {tab} requests right now.</p>
            </div>
          )}
          {!loading && filtered.map((w) => (
            <WithdrawalRow
              key={w.withdrawal_id}
              w={w}
              busy={busyId === w.withdrawal_id}
              onMarkPaid={() => setRefDialog({ mode: "pay", withdrawal: w })}
              onReject={() => setRefDialog({ mode: "reject", withdrawal: w })}
            />
          ))}
        </div>

        {refDialog && (
          <ActionDialog
            mode={refDialog.mode}
            withdrawal={refDialog.withdrawal}
            busy={busyId === refDialog.withdrawal.withdrawal_id}
            onCancel={() => setRefDialog(null)}
            onConfirm={(refOrNote, note) =>
              refDialog.mode === "pay"
                ? markPaid(refDialog.withdrawal, refOrNote, note)
                : reject(refDialog.withdrawal, refOrNote)
            }
          />
        )}
      </main>
      <Footer />
    </>
  );
}

/* ─── Subcomponents ────────────────────────────────────────────────── */

function SummaryTile({ label, count, amount, tone }) {
  const toneMap = {
    amber: "from-amber-100 to-orange-100 text-amber-900 border-amber-200",
    emerald: "from-emerald-100 to-teal-100 text-emerald-900 border-emerald-200",
    indigo: "from-indigo-100 to-blue-100 text-indigo-900 border-indigo-200",
    violet: "from-violet-100 to-fuchsia-100 text-violet-900 border-violet-200",
  };
  return (
    <div
      className={`rounded-2xl bg-gradient-to-br ${toneMap[tone] || toneMap.amber} border p-4 sm:p-5 shadow-sm`}
      // Fixed dark-on-light text — survives cosmic-night theme override
      // (which would otherwise force these labels white-on-pastel).
      style={{ color: "#1a1a2e" }}
    >
      <p className="label-eyebrow !text-[10px]" style={{ color: "#1a1a2e", opacity: 0.7 }}>{label}</p>
      <p className="font-serif-display text-2xl sm:text-3xl mt-1.5" style={{ color: "#1a1a2e" }}>{INR(amount)}</p>
      <p className="text-xs mt-0.5" style={{ color: "#1a1a2e", opacity: 0.7 }}>
        {count === "—" ? "" : `${count} request${count === 1 ? "" : "s"}`}
      </p>
    </div>
  );
}

function WithdrawalRow({ w, busy, onMarkPaid, onReject }) {
  const bal = w.astrologer_balance || {};
  const reqDate = w.requested_at ? new Date(w.requested_at).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
  }) : "—";
  const payout = w.payout_account;
  const destLabel = payout
    ? (payout.method === "upi"
        ? `UPI · ${payout.upi_vpa}`
        : `Bank · ${payout.account_number_masked} · ${payout.ifsc} · ${payout.account_holder}`)
    : null;
  return (
    <article
      className="card-surface rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid={`withdrawal-row-${w.withdrawal_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h3 className="font-serif-display text-xl text-[#3F2D1B]">{w.astrologer_name}</h3>
          <span className="text-xs text-muted">· {w.astrologer_email}</span>
        </div>
        <p className="text-xs text-muted mt-1">Requested {reqDate}</p>
        {destLabel && (
          <p
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800"
            data-testid={`withdrawal-payout-${w.withdrawal_id}`}
          >
            → Send to: <span className="font-semibold">{destLabel}</span>
          </p>
        )}
        {w.note && <p className="text-xs text-stone-700 italic mt-2">&ldquo;{w.note}&rdquo;</p>}
        {bal.lifetime_earnings !== undefined && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-stone-600">
            <span>Lifetime: <b className="text-stone-800">{INR(bal.lifetime_earnings)}</b></span>
            <span>Paid out: <b className="text-stone-800">{INR(bal.paid_out)}</b></span>
            <span>Pending: <b className="text-stone-800">{INR(bal.pending_withdrawals)}</b></span>
            <span>Available: <b className="text-stone-800">{INR(bal.available_balance)}</b></span>
          </div>
        )}
        {w.payment_reference && (
          <p className="text-[11px] text-emerald-700 mt-2">
            Paid · ref <span className="font-mono">{w.payment_reference}</span>
            {w.paid_at && <> · {new Date(w.paid_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</>}
          </p>
        )}
      </div>

      <div className="flex flex-col sm:items-end gap-2">
        <p className="font-serif-display text-3xl sm:text-4xl text-[#3F2D1B]" data-testid={`withdrawal-amount-${w.withdrawal_id}`}>
          {INR(w.amount)}
        </p>
        {w.status === "pending" && (
          <div className="flex items-center gap-2">
            <button
              onClick={onReject}
              disabled={busy}
              className="rounded-full px-3.5 py-2 text-xs font-medium border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              data-testid={`reject-btn-${w.withdrawal_id}`}
            >
              Reject
            </button>
            <button
              onClick={onMarkPaid}
              disabled={busy}
              className="rounded-full px-4 py-2 text-xs font-bold bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow disabled:opacity-50"
              data-testid={`mark-paid-btn-${w.withdrawal_id}`}
            >
              {busy ? "Saving…" : "Mark as paid"}
            </button>
          </div>
        )}
        {w.status === "paid" && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
            <CheckCircle size={14} weight="fill" /> Paid
          </span>
        )}
        {w.status === "rejected" && (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700">
            <XCircle size={14} weight="fill" /> Rejected
          </span>
        )}
      </div>
    </article>
  );
}

function ActionDialog({ mode, withdrawal, busy, onCancel, onConfirm }) {
  const [field1, setField1] = useState(""); // payment ref OR rejection note
  const [field2, setField2] = useState(""); // optional admin note (pay mode)
  const isPay = mode === "pay";
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="withdrawal-action-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={() => !busy && onCancel()} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-amber-200 p-6">
        <h2 className="font-serif-display text-2xl text-[#3F2D1B]">
          {isPay ? "Mark as paid" : "Reject request"}
        </h2>
        <p className="text-sm text-stone-700 mt-2">
          {withdrawal.astrologer_name} · <span className="font-bold">{INR(withdrawal.amount)}</span>
        </p>
        {isPay ? (
          <>
            <label className="label-eyebrow mt-5 block">Payment reference (optional)</label>
            <input
              type="text"
              value={field1}
              onChange={(e) => setField1(e.target.value)}
              placeholder="UPI / bank txn id"
              className="w-full mt-2 px-3 py-2.5 border border-stone-300 rounded-md text-sm focus:border-gold outline-none"
              data-testid="dialog-payment-ref"
            />
            <label className="label-eyebrow mt-4 block">Note (optional)</label>
            <textarea
              value={field2}
              onChange={(e) => setField2(e.target.value)}
              rows={2}
              className="w-full mt-2 px-3 py-2 border border-stone-300 rounded-md text-sm focus:border-gold outline-none"
              data-testid="dialog-pay-note"
            />
          </>
        ) : (
          <>
            <label className="label-eyebrow mt-5 block">Rejection reason</label>
            <textarea
              value={field1}
              onChange={(e) => setField1(e.target.value)}
              rows={3}
              placeholder="Tell the astrologer why this was declined"
              className="w-full mt-2 px-3 py-2 border border-stone-300 rounded-md text-sm focus:border-gold outline-none"
              data-testid="dialog-reject-note"
            />
          </>
        )}
        <div className="flex items-center gap-3 mt-6">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 btn-ghost rounded-full py-2.5 text-sm"
            data-testid="dialog-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(field1, field2)}
            disabled={busy || (!isPay && !field1.trim())}
            className={`flex-1 rounded-full py-2.5 text-sm font-bold disabled:opacity-50 ${
              isPay
                ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white"
                : "bg-gradient-to-r from-rose-500 to-rose-600 text-white"
            }`}
            data-testid="dialog-confirm"
          >
            {busy ? "Saving…" : isPay ? "Confirm paid" : "Reject request"}
          </button>
        </div>
      </div>
    </div>
  );
}
